import React from 'react';
import './PrivateAI.css';

function PrivateAI() {
  return (
    <section className="private-ai">
      <div className="section-inner">

        <div className="pai-problem-strip">
          <div className="pai-problem-item">
            <div className="pai-cost">$0.015</div>
            <div className="pai-label">Per 1K tokens (GPT-4)</div>
          </div>
          <div className="pai-problem-item">
            <div className="pai-cost">10,000+</div>
            <div className="pai-label">Queries/month (avg team)</div>
          </div>
          <div className="pai-problem-item">
            <div className="pai-cost">$3,600+</div>
            <div className="pai-label">Annual API cost (growing)</div>
          </div>
        </div>

        <div className="pai-section">
          <h2>The Problem With Rented AI</h2>
          <p>Every time your team asks ChatGPT, Claude, or Copilot a question, three things happen: you pay per token, your data leaves your network, and you get the same generic model everyone else gets.</p>
          <p>For companies handling patient records, legal documents, financial data, or proprietary processes, that's not just expensive. It's a liability. Your data trains their models. Your questions reveal your strategy. Your bill grows with every employee who finds it useful.</p>
          <div className="pai-highlight">
            <p>What if you owned the model instead? Trained on your domain. Running on your hardware. Zero tokens. Zero data exposure. The same AI capability without the subscription, the surveillance, or the scaling cost.</p>
          </div>
        </div>

        <div className="pai-section">
          <h2>What You Get</h2>
          <div className="pai-features">
            <div className="pai-feature-card pai-feature-sovereign">
              <div className="pai-feature-title">Complete Data Sovereignty</div>
              <p>Your data never leaves your building. Period. No cloud processing. No third-party servers. No "we promise we won't look." The model runs on YOUR hardware, behind YOUR firewall, under YOUR control. Every byte of patient data, every contract clause, every proprietary formula stays exactly where it belongs: with you.</p>
            </div>
            <div className="pai-feature-card">
              <div className="pai-feature-title">Zero Data Exposure</div>
              <p>When you use API-based AI, every query exposes your data to an external company. Their privacy policy is the only thing between your trade secrets and their training pipeline. With private AI, there is no external company. No data transmission. No exposure vector. Your information physically cannot leave your network.</p>
            </div>
            <div className="pai-feature-card">
              <div className="pai-feature-title">Compliance by Architecture</div>
              <p>HIPAA, SOC 2, FERPA, attorney-client privilege. When the model runs inside your network, compliance is a structural fact, not a vendor promise. Auditors don't need to evaluate a third party's security posture. The data never left your jurisdiction.</p>
            </div>
            <div className="pai-feature-card">
              <div className="pai-feature-title">No Per-Token Cost</div>
              <p>Ask it a million questions a day. The cost doesn't change. No API metering. No surprise invoices. No CFO asking why the AI bill tripled because your team actually used it.</p>
            </div>
            <div className="pai-feature-card">
              <div className="pai-feature-title">Trained On Your Domain</div>
              <p>Genesis cells trained specifically on your documentation, your processes, your terminology. Your knowledge lives inside the model's structure, not in a search index. Deeper understanding than retrieval-based approaches.</p>
            </div>
            <div className="pai-feature-card">
              <div className="pai-feature-title">Custom UI Interfaces</div>
              <p>Purpose-built interfaces designed for your team's workflow. Seamless integration into your existing systems. Your AI, your way, with a front end that makes sense for your people.</p>
            </div>
          </div>
        </div>

        <div className="pai-section">
          <h2>Rented AI vs. Private AI</h2>
          <div className="pai-comparison">
            <div className="pai-compare-row pai-header-row">
              <div className="pai-compare-cell"></div>
              <div className="pai-compare-cell pai-api">API-Based AI</div>
              <div className="pai-compare-cell pai-private">Private Model</div>
            </div>
            <div className="pai-compare-row">
              <div className="pai-compare-cell">Data location</div>
              <div className="pai-compare-cell pai-api">Their servers</div>
              <div className="pai-compare-cell pai-private">Your servers</div>
            </div>
            <div className="pai-compare-row">
              <div className="pai-compare-cell">Cost model</div>
              <div className="pai-compare-cell pai-api">Per token, forever</div>
              <div className="pai-compare-cell pai-private">One-time deployment</div>
            </div>
            <div className="pai-compare-row">
              <div className="pai-compare-cell">Domain knowledge</div>
              <div className="pai-compare-cell pai-api">Generic</div>
              <div className="pai-compare-cell pai-private">Trained on your data</div>
            </div>
            <div className="pai-compare-row">
              <div className="pai-compare-cell">Compliance</div>
              <div className="pai-compare-cell pai-api">Trust their policy</div>
              <div className="pai-compare-cell pai-private">Verify your own</div>
            </div>
            <div className="pai-compare-row">
              <div className="pai-compare-cell">Availability</div>
              <div className="pai-compare-cell pai-api">Depends on their uptime</div>
              <div className="pai-compare-cell pai-private">Runs when you run</div>
            </div>
            <div className="pai-compare-row">
              <div className="pai-compare-cell">Your data trains</div>
              <div className="pai-compare-cell pai-api">Their next model</div>
              <div className="pai-compare-cell pai-private">Only your model</div>
            </div>
            <div className="pai-compare-row">
              <div className="pai-compare-cell">Scales with usage</div>
              <div className="pai-compare-cell pai-api">Cost goes up</div>
              <div className="pai-compare-cell pai-private">Cost stays flat</div>
            </div>
            <div className="pai-compare-row">
              <div className="pai-compare-cell">Data breach liability</div>
              <div className="pai-compare-cell pai-api">Their breach = your problem</div>
              <div className="pai-compare-cell pai-private">Data never left your building</div>
            </div>
            <div className="pai-compare-row">
              <div className="pai-compare-cell">Regulatory audit</div>
              <div className="pai-compare-cell pai-api">Depends on vendor cooperation</div>
              <div className="pai-compare-cell pai-private">You control the entire stack</div>
            </div>
          </div>
        </div>

        <div className="pai-section">
          <h2>Built For</h2>
          <div className="pai-use-cases">
            <div className="pai-use-case">
              <div className="pai-use-case-title">Healthcare</div>
              <p>Patient intake processing, clinical note summarization, discharge documentation. HIPAA-compliant by design because the data never leaves your system.</p>
            </div>
            <div className="pai-use-case">
              <div className="pai-use-case-title">Legal</div>
              <p>Contract review, case research, document drafting. Trained on your firm's precedents and templates. Attorney-client privilege preserved in the architecture.</p>
            </div>
            <div className="pai-use-case">
              <div className="pai-use-case-title">Manufacturing</div>
              <p>Equipment manuals, safety protocols, maintenance logs. Your technicians ask questions in plain language. The model answers from your documentation.</p>
            </div>
            <div className="pai-use-case">
              <div className="pai-use-case-title">Financial Services</div>
              <p>Regulatory compliance, client communications, internal reporting. SOC 2 aligned. Audit trail built in. No customer data touching external servers.</p>
            </div>
          </div>
        </div>

        <div className="pai-section">
          <h2>How It Works</h2>
          <div className="pai-process">
            <div className="pai-step">
              <div className="pai-step-number">1</div>
              <div className="pai-step-title">Assess</div>
              <p>We evaluate your data, your hardware, your use case. We determine the right model size and training approach for your domain.</p>
            </div>
            <div className="pai-step">
              <div className="pai-step-number">2</div>
              <div className="pai-step-title">Train</div>
              <p>We train a custom model on your data. Your documents, your terminology, your processes. The model learns your business.</p>
            </div>
            <div className="pai-step">
              <div className="pai-step-number">3</div>
              <div className="pai-step-title">Deploy</div>
              <p>The trained model installs on your infrastructure. Your server, your network, your control. We hand you the keys and walk you through operation.</p>
            </div>
          </div>
        </div>

        <div className="pai-section">
          <h2>Why Nexus Concordat</h2>
          <p>Most "custom AI" is a generic model with a retrieval layer stapled on top. We build different. Our patent-pending architecture uses genesis cells that train directly on your domain. Your knowledge lives inside the model, not in a search index it queries at runtime. The result: faster inference, deeper domain comprehension, and no retrieval failures.</p>
          <div className="pai-highlight">
            <p>Three U.S. provisional patent applications protect the core architecture. Your private model is built on technology that exists nowhere else.</p>
          </div>
        </div>

        <div className="pai-section">
          <h2>Data Sizing + Training</h2>
          <p>Most private AI vendors bolt a retrieval layer (RAG) onto a generic model and call it "custom." We don't. Our patent-pending genesis cell architecture trains specialized neural cells on your domain data. Your knowledge becomes part of the model's structure, not a search index it references. Faster responses, deeper understanding, no retrieval latency.</p>
          <div className="pai-data-grid">
            <div className="pai-data-card">
              <div className="pai-data-value">500 MB</div>
              <div className="pai-data-label">Included training data (Starter/Beta)</div>
            </div>
            <div className="pai-data-card">
              <div className="pai-data-value">~500K</div>
              <div className="pai-data-label">Training pairs prepared</div>
            </div>
            <div className="pai-data-card">
              <div className="pai-data-value">$49/mo</div>
              <div className="pai-data-label">Beta NLM license (locked for life)</div>
            </div>
            <div className="pai-data-card">
              <div className="pai-data-value">$5/MB</div>
              <div className="pai-data-label">Additional data beyond allowance</div>
            </div>
          </div>
        </div>

        <div className="pai-section">
          <h2>Founding Client Program</h2>
          <div className="pai-beta-banner">
            <div className="pai-beta-badge">Limited Availability</div>
            <p>We are accepting our first 10 clients at founding member pricing. Up to 500 MB of training data, ~500,000 training pairs, genesis cell training, and deployment. You own the model. The optional NLM license keeps it learning from your usage with automated weekly retraining and architecture updates. Rate locked for life.</p>
            <div className="pai-beta-pricing">
              <div className="pai-beta-item">
                <span className="pai-beta-was">$3,500</span>
                <span className="pai-beta-now">$1,500</span>
                <span className="pai-beta-label">Setup</span>
              </div>
              <div className="pai-beta-item">
                <span className="pai-beta-was">$99/mo</span>
                <span className="pai-beta-now">$49/mo</span>
                <span className="pai-beta-label">NLM License</span>
              </div>
            </div>
          </div>
        </div>

        <div className="pai-section">
          <h2>Custom Interface Options</h2>
          <p>Every deployment includes a simple query interface. Need more? We build to fit your workflow. Simple HTML is included. Full React web apps start at $2,500. Enterprise platforms with email, dashboards, and multi-department views start at $5,000. Monthly add-on for React apps only applies above 30 users.</p>
        </div>

        <div className="pai-cta">
          <h2>Ready to Own Your AI?</h2>
          <p>Tell us what your team needs. We'll show you what a private model looks like for your business.</p>
          <a className="pai-cta-button" href="mailto:caelsereith@aetherprotocols.com">Get Started</a>
          <div className="pai-cta-sub">caelsereith@aetherprotocols.com</div>
        </div>

      </div>
    </section>
  );
}

export default PrivateAI;
